<!-- Superseded by ResponsiveTable.vue for new tables — see table.md. Kept as-is for its
     existing consumers (Nominations/Committees/Reports/DraftEventMembers) until migrated. -->
<template>
  <div ref="root" class="gt" :class="[{ 'gt--mob': isMob, 'gt--tab': isTab, 'gt--tab-top': tabAlignTop, 'gt--loading': loading, 'gt--no-borders': noBorders, 'gt--no-stripes': noStripes }, borderModeClass]" :style="cssVars">
    <div v-if="loading" class="table-overlay">
      <div class="table-spinner" />
    </div>

    <div v-if="!hideHeader" class="gt__row gt__row--head">
      <div
        v-for="col in columns"
        :key="col.key"
        class="gt__hdr"
        :class="[mobClass(col), tabHdrClass(col), col.cellClass, { 'gt__hdr--sort': col.sortable }]"
        @click="col.sortable ? $emit('sort', col.key) : undefined"
      >
        {{ col.label }}
        <span v-if="col.sortable" class="sort-arrows">
          <span :class="['sort-arrow sort-arrow--up', { active: sort === col.key && direction === 'asc' }]" />
          <span :class="['sort-arrow sort-arrow--down', { active: sort === col.key && direction === 'desc' }]" />
        </span>
      </div>
    </div>

    <div v-if="!loading && !displayRows.length && !groupList.length" class="gt__empty">
      {{ emptyText || 'No results found.' }}
    </div>

    <!-- Grouped draggable rows: a group moves as a whole, children only reorder inside their own unit.
         `rows` (if any) are then rendered below as plain, non-draggable rows (e.g. a totals row). -->
    <draggable
      v-if="draggable && groups"
      tag="div"
      class="gt__draggable-body"
      :list="groupList"
      handle=".gt__drag-handle--group"
      @end="onGroupDragEnd"
    >
      <div
        v-for="group in groupList"
        :key="`g-${group.id}`"
        class="gt__group"
        :class="{ 'gt__group--single': group.units.length === 1 && !group.units[0].children.length }"
      >
        <template v-for="unit in group.units">
          <!-- Optional per-row notice (e.g. the result of an action on this event) shown right above it -->
          <div v-if="$scopedSlots['before-row']" :key="`before-${unit.row.id}`" class="gt__before-row">
            <slot name="before-row" :row="unit.row" />
          </div>
          <div
            :key="String(unit.row.id)"
            :data-row-id="unit.row.id"
            class="gt__row gt__row--data"
            :class="{ 'gt__row--open': resolvedOpenId === unit.row.id, 'gt__row--odd': groupRowIndex[unit.row.id] % 2 === 0 }"
          >
            <div
              v-for="(col, i) in columns"
              :key="col.key"
              class="gt__cell"
              :class="[mobClass(col), tabCellClass(col), col.cellClass, { 'gt__cell--tab-labeled': isTab && col.tabLabel, 'gt__cell--tab-top': col.tabAlignTop }]"
              :style="tabCellStyle(col)"
            >
              <strong v-if="isTab && col.tabLabel" class="gt__tab-label">{{ col.label }}</strong>
              <slot :name="`cell:${col.key}`" :row="unit.row">{{ unit.row[col.key] }}</slot>
              <div v-if="i === primaryIdx && mobileLabelCols.length" class="gt__mob-labels">
                <div v-for="mc in mobileLabelCols" :key="mc.key" class="gt__mob-label">
                  <strong>{{ mc.label }}: </strong>
                  <slot :name="`cell:${mc.key}`" :row="unit.row">{{ unit.row[mc.key] }}</slot>
                </div>
              </div>
            </div>
          </div>
          <draggable
            :key="`u-${unit.row.id}`"
            tag="div"
            class="gt__draggable-body"
            :list="unit.children"
            :group="`unit-${unit.row.id}`"
            handle=".gt__drag-handle--child"
            @end="onGroupDragEnd"
          >
            <div
              v-for="child in unit.children"
              :key="String(child.id)"
              :data-row-id="child.id"
              class="gt__row gt__row--data gt__row--child"
              :class="{ 'gt__row--open': resolvedOpenId === child.id, 'gt__row--odd': groupRowIndex[child.id] % 2 === 0 }"
            >
              <div
                v-for="(col, i) in columns"
                :key="col.key"
                class="gt__cell"
                :class="[mobClass(col), tabCellClass(col), col.cellClass, { 'gt__cell--tab-labeled': isTab && col.tabLabel, 'gt__cell--tab-top': col.tabAlignTop }]"
                :style="tabCellStyle(col)"
              >
                <strong v-if="isTab && col.tabLabel" class="gt__tab-label">{{ col.label }}</strong>
                <slot :name="`cell:${col.key}`" :row="child">{{ child[col.key] }}</slot>
                <div v-if="i === primaryIdx && mobileLabelCols.length" class="gt__mob-labels">
                  <div v-for="mc in mobileLabelCols" :key="mc.key" class="gt__mob-label">
                    <strong>{{ mc.label }}: </strong>
                    <slot :name="`cell:${mc.key}`" :row="child">{{ child[mc.key] }}</slot>
                  </div>
                </div>
              </div>
            </div>
          </draggable>
        </template>
      </div>
    </draggable>

    <!-- Draggable rows (no expand support) -->
    <draggable
      v-if="draggable && !groups"
      tag="div"
      class="gt__draggable-body"
      :list="displayRows"
      handle=".gt__drag-handle"
      @start="$emit('drag-start', displayRows.map(r => r.id))"
      @end="$emit('drag-end', displayRows.map(r => r.id))"
    >
      <div
        v-for="(row, index) in displayRows"
        :key="String(row.id)"
        class="gt__row gt__row--data"
        :class="{ 'gt__row--open': resolvedOpenId === row.id, 'gt__row--odd': index % 2 === 0 }"
      >
        <div
          v-for="(col, i) in columns"
          :key="col.key"
          class="gt__cell"
          :class="[mobClass(col), tabCellClass(col), col.cellClass, { 'gt__cell--tab-labeled': isTab && col.tabLabel, 'gt__cell--tab-top': col.tabAlignTop }]"
          :style="tabCellStyle(col)"
        >
          <strong v-if="isTab && col.tabLabel" class="gt__tab-label">{{ col.label }}</strong>
          <slot :name="`cell:${col.key}`" :row="row">{{ row[col.key] }}</slot>
          <div v-if="i === primaryIdx && mobileLabelCols.length" class="gt__mob-labels">
            <div v-for="mc in mobileLabelCols" :key="mc.key" class="gt__mob-label">
              <strong>{{ mc.label }}: </strong>
              <slot :name="`cell:${mc.key}`" :row="row">{{ row[mc.key] }}</slot>
            </div>
          </div>
        </div>
      </div>
    </draggable>

    <!-- Normal rows with expand support -->
    <template v-if="!draggable || groups">
    <template v-for="(row, index) in displayRows">
      <div v-if="$scopedSlots['before-row']" :key="`before-${row.id}`" class="gt__before-row">
        <slot name="before-row" :row="row" />
      </div>
      <div
        :key="String(row.id)"
        class="gt__row gt__row--data"
        :class="{ 'gt__row--open': resolvedOpenId === row.id, 'gt__row--odd': (index + groupRowCount) % 2 === 0 }"
      >
        <div
          v-for="(col, i) in columns"
          :key="col.key"
          class="gt__cell"
          :class="[mobClass(col), tabCellClass(col), col.cellClass, { 'gt__cell--tab-labeled': isTab && col.tabLabel, 'gt__cell--tab-top': col.tabAlignTop }]"
          :style="tabCellStyle(col)"
        >
          <strong v-if="isTab && col.tabLabel" class="gt__tab-label">{{ col.label }}</strong>
          <slot
            :name="`cell:${col.key}`"
            :row="row"
            :toggle="() => toggleRow(row.id)"
            :is-open="resolvedOpenId === row.id"
          >{{ row[col.key] }}</slot>
          <div v-if="i === primaryIdx && mobileLabelCols.length" class="gt__mob-labels">
            <div v-for="mc in mobileLabelCols" :key="mc.key" class="gt__mob-label">
              <strong>{{ mc.label }}: </strong>
              <slot :name="`cell:${mc.key}`" :row="row">{{ row[mc.key] }}</slot>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasExpand && resolvedOpenId === row.id"
        :key="`x-${row.id}`"
        class="gt__expand"
      >
        <slot name="expand" :row="row" :odd="(index + groupRowCount) % 2 === 0" />
      </div>
    </template>
    </template>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from '@vue/composition-api'
import Draggable from 'vuedraggable'

export default defineComponent({
  name: 'GridTable',
  components: { Draggable },

  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    sort: { type: String, default: null },
    direction: { type: String, default: 'asc' },
    emptyText: { type: String, default: null },
    openId: { type: [String, Number], default: undefined },
    draggable: { type: Boolean, default: false },
    /**
     * Opt-in grouped drag mode (with `draggable`): [{ id, units: [{ row, children: [] }] }].
     * The row's `cell:*` slot decides who may be dragged by putting `gt__drag-handle--group`
     * (moves the whole group) or `gt__drag-handle--child` (reorders inside its own unit) on
     * the handle element. `rows` is then rendered below as plain, non-draggable rows.
     * Emits `drag-end` with the flat ordered ids of everything inside the groups.
     */
    groups: { type: Array, default: null },
    hideHeader: { type: Boolean, default: false },
    noBorders: { type: Boolean, default: false },
    noStripes: { type: Boolean, default: false },
    borderMode: { type: String, default: null },
    borderColor: { type: String, default: null },
    borderWidth: { type: String, default: null },
    rowBg: { type: String, default: null },
    tabAlignTop: { type: Boolean, default: false },
    mobBreak: { type: Number, default: 575 },
    tabBreak: { type: Number, default: 767 }
  },

  setup(props) {
    const { proxy } = getCurrentInstance()
    const root = ref(null)
    const isMob = ref(false)
    const isTab = ref(false)
    const internalOpenId = ref(null)

    let ro = null

    onMounted(() => {
      ro = new ResizeObserver(([entry]) => {
        const w = entry.contentRect.width
        isMob.value = w <= props.mobBreak
        isTab.value = !isMob.value && w <= props.tabBreak
      })
      ro.observe(root.value)
    })

    onUnmounted(() => {
      if (ro) ro.disconnect()
    })

    const cachedRows = ref([])
    watch(
      [() => props.rows, () => props.loading],
      ([rows, loading]) => { if (!loading) cachedRows.value = rows },
      { immediate: true }
    )
    const displayRows = computed(() => (props.loading ? cachedRows.value : props.rows))

    // Local mutable copy: vuedraggable splices the arrays it is given in place.
    const groupList = ref([])
    watch(
      () => props.groups,
      (groups) => {
        groupList.value = (groups || []).map(group => ({
          ...group,
          units: group.units.map(unit => ({ ...unit, children: [...unit.children] }))
        }))
      },
      { immediate: true }
    )

    // Row position across all groups, so stripes alternate row by row exactly like the plain table.
    const groupRowIndex = computed(() => {
      const index = {}
      let n = 0
      groupList.value.forEach(group => group.units.forEach((unit) => {
        index[unit.row.id] = n++
        unit.children.forEach((child) => { index[child.id] = n++ })
      }))
      return index
    })

    const groupRowCount = computed(() => Object.keys(groupRowIndex.value).length)

    // Second argument: the id of the row that was dragged (a group's first row, or the child row), so
    // the page can show the result of the reorder next to it.
    function onGroupDragEnd(evt) {
      const ids = groupList.value.flatMap(group => group.units.flatMap(unit => [unit.row.id, ...unit.children.map(child => child.id)]))
      const item = evt && evt.item
      const holder = item && (item.dataset.rowId != null ? item : item.querySelector('[data-row-id]'))
      const movedId = holder ? Number(holder.dataset.rowId) : null
      proxy.$emit('drag-end', ids, Number.isNaN(movedId) ? null : movedId)
    }

    const resolvedOpenId = computed(() => (
      props.openId !== undefined ? props.openId : internalOpenId.value
    ))

    function toggleRow(id) {
      if (props.openId !== undefined) return
      internalOpenId.value = internalOpenId.value === id ? null : id
    }

    function colTrack(col, widthKey = 'width') {
      const w = col[widthKey] || col.width || 'max-content'
      return col.minWidth ? `minmax(${col.minWidth}, ${w})` : w
    }

    const cssVars = computed(() => {
      const vars = {
        '--gt-cols': props.columns.map(c => colTrack(c)).join(' '),
        '--gt-cols-mob': props.columns.filter(c => !c.mobile).map(c => colTrack(c, 'mobileWidth')).join(' '),
        '--gt-cols-tab': props.columns.filter(c => !c.tabRow && c.tablet !== 'hide' && c.tablet !== 'wrap').map(c => colTrack(c, 'tabWidth')).join(' ')
      }
      if (props.borderColor != null) vars['--gt-border-color'] = props.borderColor
      if (props.borderWidth != null) vars['--gt-border-width'] = props.borderWidth
      if (props.rowBg != null) vars['--gt-row-bg'] = props.rowBg
      return vars
    })

    const mobileLabelCols = computed(() => props.columns.filter(c => c.mobile === 'label'))

    const primaryIdx = computed(() => {
      const explicit = props.columns.findIndex(c => c.primary)
      return explicit !== -1 ? explicit : props.columns.findIndex(c => !c.mobile)
    })

    const hasExpand = computed(() => 'expand' in proxy.$scopedSlots)

    function mobClass(col) {
      if (!col.mobile) return null
      return col.mobile === 'label' ? 'gt__cell--mob-label' : 'gt__cell--mob-hide'
    }

    function tabHdrClass(col) {
      if (col.tablet === 'wrap' || col.tabRow) return 'gt__hdr--tab-wrap'
      if (col.tablet === 'hide') return 'gt__hdr--tab-hide'
      if (col.tabHideLabel) return 'gt__hdr--tab-no-label'
      return null
    }

    function tabCellClass(col) {
      if (col.tablet === 'wrap' || col.tabRow) return 'gt__cell--tab-wrap'
      if (col.tablet === 'hide') return 'gt__cell--tab-hide'
      return null
    }

    const tabWrapLayout = computed(() => {
      const n = props.columns.filter(c => !c.tabRow && c.tablet !== 'hide' && c.tablet !== 'wrap').length

      function calcRow(cols, row) {
        return cols.reduce((acc, col, i, arr) => {
          const prevEnd = i === 0 ? 1 : acc[arr[i - 1].key].end
          const isLast = i === arr.length - 1
          const pct = col.tabletWidth ? parseFloat(col.tabletWidth) / 100 : 1 / arr.length
          const span = isLast ? (n + 1) - prevEnd : Math.max(1, Math.round(pct * n))
          acc[col.key] = { start: prevEnd, end: prevEnd + span, row }
          return acc
        }, {})
      }

      const rowNumbers = [...new Set(props.columns
        .map(c => (c.tablet === 'wrap' ? 2 : c.tabRow))
        .filter(row => row != null))]

      return rowNumbers.reduce((acc, row) => {
        const cols = props.columns.filter(c => (c.tablet === 'wrap' ? 2 : c.tabRow) === row)
        return { ...acc, ...calcRow(cols, row) }
      }, {})
    })

    function tabCellStyle(col) {
      const pos = tabWrapLayout.value[col.key]
      if (!pos) return {}
      return { '--tab-col-start': pos.start, '--tab-col-end': pos.end, '--tab-row': pos.row }
    }

    const borderModeClass = computed(() => {
      if (!props.borderMode) return null
      return `gt--border-${props.borderMode}`
    })

    return { root, isMob, isTab, displayRows, groupList, groupRowIndex, groupRowCount, onGroupDragEnd, resolvedOpenId, toggleRow, cssVars, mobileLabelCols, primaryIdx, hasExpand, mobClass, tabHdrClass, tabCellClass, tabCellStyle, borderModeClass }
  }
})
</script>



// WEBPACK FOOTER //
// src/components/GridTable.vue